import { FC, useCallback, useState } from 'react';
import Page, { PageProps } from '@/components/page';
import DataTable from '@/components/data-table';
import { DtColumn, DtFilter, DtPage } from '@/components/data-table/helpers';
import StatusChip, { ChipStatus } from '@/components/chip/status-chip';
import { getProviders } from '@/lib/services/context-provider.service';
import { getTypedContextProvider } from '@/lib/models/context-provider';
import OpenDrawerButton from '@/components/drawers/open-drawer-button';
import { QueryKey } from '@/lib/query-client';
import Button from '@/components/button';
import PageHeader from '@/components/page/header';
import Tooltip from '@/components/tooltip';
import DomSize from '@/components/dom-size';
import useFeature from '@/hooks/use-feature.hook';
import PageFeatureToast from '@/components/page-feature-toast';
import { useQuery } from 'react-query';
import { getContextProviderConnectors } from '@/lib/services/connector.service';
import useQueryHelper from '@/hooks/use-query-helper';
import NoneChip from '@/components/chip/none-chip';
import PageBlurb from '@/components/page/blurb';
import PageTools from '@/components/page/tools';
import useDrawer from '@/hooks/use-drawer.hook';
import ConnectorLink from '@/components/connector-link';

const columns: DtColumn[] = [
  { name: 'name', label: 'Name', sortable: true },
  { name: 'type', label: 'Type', sortable: true },
  { name: 'connector', label: 'Connector', sortable: false },
  { name: 'status', label: 'Status', sortable: true },
  { name: 'action', label: '', flex: 0.5, sortable: false },
];

const ContextProvidersPage: FC<PageProps> = () => {
  const { openDrawer } = useDrawer();
  const [selectedRowId, setSelectedRowId] = useState('');
  const { canChangeContextProviders, getTooltip, loading: featureLoading } = useFeature();

  const connectorQuery = useQuery([QueryKey.ConnectorProvidersList], async () =>
    getContextProviderConnectors()
  );
  const { showLoader: connectorsLoading } = useQueryHelper(connectorQuery);
  const { data: connectors } = connectorQuery;

  const handleView = useCallback(
    (providerId?: string) => {
      openDrawer('context-provider', {
        id: providerId || null,
        onClose: () => {
          setSelectedRowId('');
        },
      });
      setSelectedRowId(providerId || '');
    },
    [openDrawer]
  );

  const loadData = useCallback(
    async (pageParams: DtPage, filter?: DtFilter) => {
      const { page, pageSize, sort } = pageParams;
      const dataset = await getProviders(page, pageSize, sort, filter);
      return {
        ...dataset,
        rows: dataset.rows.map((model) => {
          const { id, name, typeLabel, status, connectorId } = getTypedContextProvider(model);
          const connector = connectors?.find(({ id }) => id === connectorId);
          const hasConnector = !!connector;

          return {
            id,
            name,
            type: typeLabel,
            connector: hasConnector ? (
              <ConnectorLink connector={connector} action="none" />
            ) : (
              <NoneChip notAvailable />
            ),
            status: <StatusChip value={status as ChipStatus} size="small" />,
            action: <OpenDrawerButton onClick={() => handleView(id)} />,
          };
        }),
      };
    },
    [handleView, connectors]
  );

  if (featureLoading || connectorsLoading) {
    return null;
  }

  return (
    <Page title="Data Sources">
      <PageHeader>
        <PageBlurb>
          Create new data sources, and view and change settings for existing data sources.
        </PageBlurb>

        <PageTools>
          <Tooltip title={getTooltip('add-context-provider')} disabled={canChangeContextProviders}>
            <Button
              label="Add Data Source"
              icon="plus"
              size="small"
              onClick={() => handleView()}
              disabled={!canChangeContextProviders}
            />
          </Tooltip>
        </PageTools>
      </PageHeader>
      <PageFeatureToast featureId="change-context-provider" can={canChangeContextProviders} />
      <DomSize>
        <DataTable
          queryKey={QueryKey.ContextProvidersDataset}
          columns={columns}
          onLoad={loadData}
          rowHeight={70}
          search
          selectedRowId={selectedRowId}
          poll={15000}
        />
      </DomSize>
    </Page>
  );
};

export default ContextProvidersPage;
